import { useMutation } from '@tanstack/react-query'

import { apiClient } from '../apiClient'

/**
 * Workflow event types tracked during mission specialist sessions
 */
export type WorkflowEventType =
  // Task lifecycle events (from dispatcher)
  | 'TASK_CREATED'
  | 'TASK_ASSIGNED'
  | 'TASK_UNASSIGNED'
  | 'TASK_CLOSED'
  | 'TASK_DISCARDED'
  | 'TASK_PREEMPTED'
  | 'TASK_STALLED'

  // Operator session events (from frontend)
  | 'CLAIMED'
  | 'PRESENCE_PROMPT_SHOWN'
  | 'PRESENCE_CONFIRMED'
  | 'PAGE_REFRESHED'
  | 'RECONNECTED'
  | 'SESSION_ENDED'

/**
 * Metadata for workflow log entries
 */
export interface WorkflowLogMetadata {
  taskId: number | string
  engagementId: string
  reason?: 'USER_LEFT' | 'NETWORK_DISCONNECTED' | 'BROWSER_CLOSED' | 'OPERATOR_TIMEOUT' | 'OPERATOR_CHECKOUT'
  context?: 'labeler' | string
  idleDuration?: number // milliseconds idle before presence prompt
  liveTaskId?: string // TASK_PREEMPTED: the live task that displaced this one
  deadline?: string // TASK_STALLED: ISO time the assignment crossed its stalled threshold
}

/**
 * Batch log format for API submission
 */
export interface BatchLogEntry {
  clientLogId: string
  eventType: WorkflowEventType
  occurredAt: string
  metadata: WorkflowLogMetadata
}

export function useBatchSync() {
  return useMutation({
    mutationFn: async (logs: BatchLogEntry[]) => {
      const { data } = await apiClient.post('/int/v3/workflow-logs/batch', { logs })
      return data
    },
  })
}
